import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature fleet-profile
export * from './truck';
export * from './driver';
export * from './vendor';
export * from './trailer';
//////////////////////////////////////////////////

const store = ({ fleetProfile }) => fleetProfile;

// common
const makeSelectCollapsedGroup = () => createSelector(
  store,
  R.pathOr({}, ['collapsedGroup']),
);

const makeSelectGeneralDetails = () => createSelector(
  store,
  ({ type, ...state }) => R.path([type, 'generalDetails'], state),
);

const selectGeneralDetails = createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'generalDetails'], fleetProfile),
  (generalDetails) => ({ ...generalDetails }),
);

const makeSelectPrimaryObjectGuid = () => createSelector(
  store,
  (state) => {
    const { type } = state;

    return R.path([type, 'generalDetails', GC.FIELD_GUID], state);
  },
);

const makeSelectEntityBranchGuid = () => createSelector(
  store,
  (state) => {
    const { type } = state;

    return R.path([type, 'generalDetails', GC.BRANCH_GUID], state);
  },
);

const makeSelectVinLookup = () => createSelector(
  store,
  R.prop('vinLookup'),
);

// driver assignment
const makeSelectAssignment = () => createSelector(
  store,
  R.path(['assignment']),
);

const makeSelectAssignedTruckGuid = () => createSelector(
  store,
  R.path(['assignment', GC.SYSTEM_OBJECT_TRUCK, GC.FIELD_GUID]),
);

const makeSelectAvailableAssignable = () => createSelector(
  store,
  R.path(['availableAssignable']),
);

const makeSelectTrailersIntegrations = createSelector(
  [
    ({ fleetProfile: { type, ...rest } = {} }) => R.prop(type, rest),
    R.pathOr({}, ['fleetProfile', 'assignment']),
  ],
  (profile, { trailers }) => {
    const trailersIntegrations = R.map(({ guid, unitId }) => ({
      guid,
      unitId,
      itemList: R.path(['trailerIntegrationList', guid], profile),
    }), trailers);

    return trailersIntegrations;
  },
);

// vendor
const makeSelectFleetVendorList = () => createSelector(
  store,
  R.path(['fleetVendorList']),
);

// configs
const makeSelectAccessorialConfigs = () => createSelector(
  store,
  R.path(['accessorialConfigs']),
);

const makeSelectBranchConfigs = () => createSelector(
  store,
  R.path(['branchConfigs']),
);

const makeSelectConfigGroup = () => createSelector(
  store,
  R.pathOr({}, ['configGroup']),
);

// report
const makeSelectListLoadingByGroupName = (groupName) => createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'loading'], fleetProfile),
  (loading) => G.getPropFromObject(groupName, loading),
);

const makeSelectTableTitleFiltersByGroupName = (groupName) => createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'tableTitleFilters'], fleetProfile),
  (tableTitleFilters) => G.getPropFromObject(groupName, tableTitleFilters),
);

const makeSelectTitleSortValuesByGroupName = (groupName) => createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'tableTitleSortValues'], fleetProfile),
  (tableTitleSortValues) => G.getPropFromObject(groupName, tableTitleSortValues),
);

const makeSelectPaginationByGroupName = (groupName) => createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'pagination'], fleetProfile),
  (pagination) => G.getPropFromObject(groupName, pagination),
);

const makeSelectTotalCountByGroupName = (groupName) => createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'totalCount'], fleetProfile),
  (totalCount) => G.getPropFromObject(groupName, totalCount),
);

const makeSelectUsedReportByGroupName = (groupName) => createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'usedReport'], fleetProfile),
  (usedReport) => G.getPropFromObject(groupName, usedReport),
);

const makeSelectAvailableReportsByGroupName = (groupName) => createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'availableReports'], fleetProfile),
  (availableReports) => G.getPropFromObject(groupName, availableReports),
);

const makeSelectReportStatusByGroupName = (groupName) => createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'reportPending'], fleetProfile),
  (reportPending) => G.getPropFromObject(groupName, reportPending),
);

// filters
const makeSelectDocumentsFilter = () => createSelector(
  store,
  (state) => {
    const { type } = state;

    return R.pathOr('', [type, 'formGroupFilters', 'documentsFilter'], state);
  },
);

const selectFormGroupFilters = createSelector(
  ({ fleetProfile: { type, ...fleetProfile } }) => R.path([type, 'formGroupFilters'], fleetProfile),
  (formGroupFilters) => ({ ...formGroupFilters }),
);

// trucks by vendor options
const makeSelectTruckOptions = () => createSelector(
  store,
  ({ truckOptions }) => R.or(truckOptions, []),
);

export {
  // common
  makeSelectVinLookup,
  selectGeneralDetails,
  makeSelectConfigGroup,
  makeSelectCollapsedGroup,
  makeSelectGeneralDetails,
  makeSelectEntityBranchGuid,
  makeSelectPrimaryObjectGuid,
  // assignment
  makeSelectAssignment,
  makeSelectAssignedTruckGuid,
  makeSelectAvailableAssignable,
  makeSelectTrailersIntegrations,
  // vendor
  makeSelectTruckOptions,
  makeSelectFleetVendorList,
  // accessorial configs
  makeSelectAccessorialConfigs,
  // branch configs
  makeSelectBranchConfigs,
  // report
  makeSelectPaginationByGroupName,
  makeSelectTotalCountByGroupName,
  makeSelectUsedReportByGroupName,
  makeSelectListLoadingByGroupName,
  makeSelectReportStatusByGroupName,
  makeSelectTitleSortValuesByGroupName,
  makeSelectAvailableReportsByGroupName,
  makeSelectTableTitleFiltersByGroupName,
  // filters
  selectFormGroupFilters,
  makeSelectDocumentsFilter,
};
